/**
 * Turns the parsed file plus the review choices into the request body.
 * Both platforms call this, so web and mobile cannot drift on what an
 * import means.
 */
import type {
  GroupCategory,
  SplitwiseImportRequestDto,
  SplitwiseImportRowDto,
} from '@splitfiesta/shared';
import { roundToCurrencyPrecision } from '@splitfiesta/shared';

import { reconciledImportNets } from './importConversion';
import { normalizeLabel } from './labelResolution';
import type { LabelResolutions } from './labelResolution';
import { reconstructSplitwisePayment } from './reconstructSplitwiseRow';
import {
  formatTaxonomyPath,
  mapSplitwiseCategory,
  parseTaxonomyPath,
} from './splitwiseCategories';
import type { SplitwiseFile, SplitwiseRow } from './splitwiseCsv';

const TITLE_MAX = 100;

/** The override value that files a label's rows as payments between members. */
export const PAYMENT_OVERRIDE = 'payment' as const;

export interface ImportReviewState {
  groupName: string;
  groupCurrency: string;
  /** One of the app's own categories; seeded to `friends`, always set. */
  groupCategory: GroupCategory;
  /** Column index of the "That's me" member; every other column is a placeholder. */
  selfColumn: number;
  memberNames: string[];
  /** `normalizeLabel(label)` → taxonomy path, or `PAYMENT_OVERRIDE`. */
  categoryOverrides: Record<string, string>;
  /** Row index of a no-balance-effect row → the member column that paid it. */
  zeroRowAssignments: Record<number, number>;
}

/**
 * Defence in depth: a `-1` column must never reach the payload. Unreachable
 * for a parsed file — the parser classifies on nets rounded to the row
 * currency, so a payment without a positive/negative pair arrives already
 * skipped — but the builder is a public entry point and trusts `kind`.
 */
function isImportablePayment(row: SplitwiseRow): boolean {
  const { fromColumn, toColumn } = reconstructSplitwisePayment(
    row.cost,
    row.effects,
    row.currency
  );
  return fromColumn >= 0 && toColumn >= 0;
}

/**
 * The label is a CSV cell, so an unguarded lookup would resolve `toString` up
 * the prototype chain.
 */
function overrideFor(
  row: SplitwiseRow,
  overrides: Record<string, string>
): string | undefined {
  const key = normalizeLabel(row.category);
  return Object.hasOwn(overrides, key) ? overrides[key] : undefined;
}

/**
 * The kind a row imports as once the review has spoken, mirroring the
 * parser's own rule so a flip needs no re-parse: a payment needs exactly one
 * payer and one payee at the row currency's precision.
 */
export function effectiveKind(
  row: SplitwiseRow,
  overrides: Record<string, string>
): SplitwiseRow['kind'] {
  const override = overrideFor(row, overrides);
  if (override === undefined) return row.kind;
  const nets = row.effects.map(net =>
    roundToCurrencyPrecision(net, row.currency)
  );
  if (override === PAYMENT_OVERRIDE) {
    const positives = nets.filter(net => net > 0).length;
    const negatives = nets.filter(net => net < 0).length;
    return positives === 1 && negatives === 1 ? 'payment' : 'skipped';
  }
  const resolvedAsPayment =
    row.kind === 'payment' || row.skipReason === 'payment-shape';
  // A payment-shape row can have every net at zero, and an expense with
  // nothing to split is not one the server accepts.
  if (resolvedAsPayment) {
    return nets.some(net => net !== 0) ? 'expense' : 'skipped';
  }
  return row.kind;
}

/** A no-balance-effect row the reviewer has given a payer, so it imports. */
export function isAssignedZeroRow(
  row: SplitwiseRow,
  zeroRowAssignments: Record<number, number>,
  overrides: Record<string, string> = {}
): boolean {
  return (
    row.skipReason === 'no-balance-effect' &&
    zeroRowAssignments[row.rowIndex] !== undefined &&
    // Flipped to a payment it cannot form, the row is skipped for its shape,
    // and a payer would smuggle it in as an expense.
    overrideFor(row, overrides) !== PAYMENT_OVERRIDE
  );
}

function withKind(row: SplitwiseRow, kind: SplitwiseRow['kind']): SplitwiseRow {
  if (kind === 'skipped') return { ...row, kind };
  const { skipReason: _skipReason, ...rest } = row;
  return { ...rest, kind };
}

/** Each row carries its effective kind, which is what reconciliation and the previews read. */
export function importableRows(
  file: SplitwiseFile,
  zeroRowAssignments: Record<number, number> = {},
  overrides: Record<string, string> = {}
): SplitwiseRow[] {
  const rows: SplitwiseRow[] = [];
  for (const row of file.rows) {
    const kind = effectiveKind(row, overrides);
    const importable =
      kind === 'expense' ||
      (kind === 'payment'
        ? isImportablePayment(row)
        : isAssignedZeroRow(row, zeroRowAssignments, overrides));
    if (importable) rows.push(kind === row.kind ? row : withKind(row, kind));
  }
  return rows;
}

/**
 * An override, then the file's resolution, each re-read through the taxonomy
 * so a stale or hand-edited choice cannot put a path in the payload that the
 * server would not accept. `PAYMENT_OVERRIDE` is a kind, not a path: payments
 * keep the category they always carried.
 */
function categoryFor(
  row: SplitwiseRow,
  overrides: Record<string, string>,
  resolutions: LabelResolutions
): string {
  const override = overrideFor(row, overrides);
  if (override !== undefined && override !== PAYMENT_OVERRIDE) {
    return formatTaxonomyPath(parseTaxonomyPath(override));
  }
  const label = normalizeLabel(row.category);
  const resolution = Object.hasOwn(resolutions, label)
    ? resolutions[label]
    : undefined;
  return formatTaxonomyPath(
    resolution?.kind === 'category'
      ? parseTaxonomyPath(resolution.path)
      : mapSplitwiseCategory(row.category)
  );
}

function toRowDto(
  row: SplitwiseRow,
  overrides: Record<string, string>,
  resolutions: LabelResolutions,
  paidBy: number | undefined,
  nets: number[]
): SplitwiseImportRowDto {
  return {
    rowIndex: row.rowIndex,
    date: row.date,
    title: (row.description.trim() || row.category.trim() || 'Splitwise')
      .slice(0, TITLE_MAX)
      .trim(),
    category: categoryFor(row, overrides, resolutions),
    cost: roundToCurrencyPrecision(row.cost, row.currency),
    currency: row.currency,
    effects: nets
      .map((net, column) => ({ column, net }))
      .filter(effect => effect.net !== 0),
    kind: row.kind,
    ...(paidBy !== undefined ? { paidBy } : {}),
  };
}

export function buildImportPayload(
  file: SplitwiseFile,
  review: ImportReviewState
): SplitwiseImportRequestDto {
  const groupCurrency = review.groupCurrency.trim().toUpperCase();
  const rows = importableRows(
    file,
    review.zeroRowAssignments,
    review.categoryOverrides
  );
  // Rounding each row on its own drifts the per-member totals by a minor unit
  // a row; the payload carries the corrected nets so the group opens on the
  // balances the file shows. The server re-runs the same pass regardless.
  const reconciled = reconciledImportNets(rows, groupCurrency);
  return {
    groupName: review.groupName.trim(),
    groupCurrency,
    groupCategory: review.groupCategory,
    members: review.memberNames.map((name, column) => ({
      column,
      name: name.trim(),
      mapTo:
        column === review.selfColumn
          ? ('self' as const)
          : ('placeholder' as const),
    })),
    rows: rows.map(row =>
      toRowDto(
        row,
        review.categoryOverrides,
        file.resolutions,
        // Only a skipped row may carry one: the server rejects `paidBy` on an
        // expense whose index a stale assignment happens to share.
        row.kind === 'skipped'
          ? review.zeroRowAssignments[row.rowIndex]
          : undefined,
        reconciled.byRowIndex.get(row.rowIndex)?.nets ?? []
      )
    ),
  };
}
